import AppContainer from "../Structure/AppContainer";
import React, { useContext, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../Common/Loader";
import AuthContext from "../../Context/AuthContext";
import ajaxRequisitions from "../../util/remote/ajaxRequisitions";
import ajaxSuppliers from "../../util/remote/ajaxSuppliers";
import ajaxPurchaseOrders from "../../util/remote/ajaxPurchaseOrders";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
const CreatePurchaseOrder = () => {
  const { userId } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // Form fields
  const [ReqID, setReqID] = useState("");
  const [supID, setSupID] = useState("");
  const [dateCreated, setDateCreated] = useState(() => {
    const today = new Date();
    return today.toLocaleDateString("en-CA"); // YYYY-MM-DD local
  });

  // Rows state
  const [rows, setRows] = useState([]);
  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const fetchSuppliersList = async () => {
    const server_response = await ajaxSuppliers.fetchSuppliersListMini_data({});
    if (server_response.status === "OK") {
      setSuppliers(server_response.details);
    } else {
      toast.error(server_response.message);
    }
  };
  useEffect(() => {
    fetchSuppliersList();
  }, []);
  // Requisitions
  const [requisitions, setRequisitions] = useState([]);
  const fetchRequisitions_ready_to_order = async () => {
    const server_response =
      await ajaxRequisitions.fetchRequisitions_ready_to_order({});
    if (server_response.status === "OK") {
      setRequisitions(
        Array.isArray(server_response.details)
          ? server_response.details
          : requisitions
      );
    } else {
      setRequisitions([]);
      toast.error(server_response.message);
    }
  };
  useEffect(() => {
    fetchRequisitions_ready_to_order();
  }, []);
  // Fetch requisition items
  const [requisitionsItems, setRequisitionsItems] = useState([]);
  const fetchingRequisitionItems = async () => {
    if (!ReqID) {
      setRows([]);
      return;
    }
    try {
      const server_response = await ajaxRequisitions.fetchRequisitionItems({
        reqID: ReqID,
      });
      if (server_response.status === "OK") {
        setRequisitionsItems(
          Array.isArray(server_response.details) ? server_response.details : []
        );
      } else {
        setRequisitionsItems([]);
        toast.error(
          server_response.message || "Failed to fetch requisition items"
        );
      }
    } catch (error) {
      setRequisitionsItems([]);
      toast.error("Error fetching requisition items");
    }
  };
  useEffect(() => {
    fetchingRequisitionItems();
  }, [ReqID]);
  // Populate rows from requisition items
  useEffect(() => {
    if (requisitionsItems.length > 0) {
      const mappedRows = requisitionsItems.map((item) => ({
        id: item.id,
        reqID: item.reqID,
        item_name: item.item_name,
        quantity: item.quantity,
        unit: item.unit || "", // <- added unit
        estimated_cost: item.est_unit_cost,
        actual_cost: item.actual_cost || item.est_unit_cost,
      }));
      setRows(mappedRows);
    } else {
      setRows([]);
    }
  }, [requisitionsItems]);
  // Handle actual cost change
  const handleRowChange = (index, value) => {
    const updatedRows = [...rows];
    updatedRows[index].actual_cost = value;
    setRows(updatedRows);
  };
  // Calculations
  const calculateRowSubtotal = (row) =>
    (parseFloat(row.quantity) || 0) * (parseFloat(row.actual_cost) || 0);
  const calculateTotal = () =>
    rows.reduce((total, row) => total + calculateRowSubtotal(row), 0);
  const formatMoney = (value) =>
    value.toLocaleString("en-UG", {
      style: "currency",
      currency: "UGX",
      minimumFractionDigits: 2,
    });
  const allRowsFilled = () =>
    ReqID && supID && rows.length > 0 && rows.every((row) => row.actual_cost);
  // Submit
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!allRowsFilled()) {
      toast.error("Please complete all required fields");
      return;
    }
    const data = {
      reqID: ReqID,
      supplierID: supID,
      date_created: dateCreated,
      created_by: userId,
      order_items: rows.map((row) => ({
        id: row.id,
        reqID: row.reqID,
        item_name: row.item_name,
        quantity: Number(row.quantity),
        unit: row.unit, // <- send unit
        estimated_cost: Number(row.estimated_cost),
        actual_cost: Number(row.actual_cost),
      })),
    };
    setLoading(true);
    const response = await ajaxPurchaseOrders.createPurchaseOrder(data);
    setLoading(false);
    if (response.status === "OK") {
      toast.success(response.message);
      resetForm();
    } else {
      toast.error(response.message);
    }
  };
  const resetForm = () => {
    setReqID("");
    setSupID("");
    setRows([]);
    setDateCreated(new Date().toISOString().split("T")[0]);
  };
  return (
    <AppContainer title="Create Purchase Order">
      <div className="container-fluid">
        <Toaster />
        <div className="row mb-3">
          <div className="col-lg-3">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate("/purchase_orders/view_saved")}
            >
              View Purchase Orders
            </button>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="card custom-card" style={{ borderRadius: "10px" }}>
              <div className="card-body">
                <h6 className="card-title mb-4">Create Purchase Order</h6>
                <form onSubmit={handleAdd}>
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label>
                        Requisition <span style={{ color: "red" }}>*</span>
                      </label>
                      <Select
                        onChange={(e) => setReqID(e?.id)}
                        getOptionLabel={(option) => option.naration || ""}
                        getOptionValue={(option) => option.id || ""}
                        isSearchable
                        options={
                          Array.isArray(requisitions) ? requisitions : []
                        }
                        value={
                          Array.isArray(requisitions)
                            ? requisitions.find(
                                (value) => value.id === ReqID
                              ) || null
                            : null
                        }
                        placeholder="Select Requisition"
                      />
                    </div>
                    <div className="col-md-6">
                      <label>
                        Supplier <span style={{ color: "red" }}>*</span>
                      </label>
                      <Select
                        onChange={(e) => setSupID(e?.id)}
                        getOptionLabel={(option) => option.buss_name || ""}
                        getOptionValue={(option) => option.id || ""}
                        isSearchable
                        options={Array.isArray(suppliers) ? suppliers : []}
                        value={
                          Array.isArray(suppliers)
                            ? suppliers.find((value) => value.id === supID) ||
                              null
                            : null
                        }
                        placeholder="Select Supplier"
                      />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label>
                        Date Created <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={dateCreated}
                        onChange={(e) => setDateCreated(e.target.value)}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                  {/* Items Table */}
                  <div className="mb-4 col-md-12">
                    <table className="table table-bordered table-hover text-nowrap mg-b-0">
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Item Name</th>
                          <th>Quantity</th>
                          <th>Unit</th> {/* <-- added unit column */}
                          <th>Estimated Unit Cost</th>
                          <th>Actual Unit Cost</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <input
                                className="form-control"
                                type="text"
                                value={row.item_name}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                type="number"
                                value={row.quantity}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                type="text"
                                value={row.unit}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                type="number"
                                value={row.estimated_cost}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                type="number"
                                min={0}
                                value={row.actual_cost}
                                onChange={(e) =>
                                  handleRowChange(index, e.target.value)
                                }
                                required
                              />
                            </td>
                            <td>{formatMoney(calculateRowSubtotal(row))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Total */}
                  <div className="mb-4 col-md-12">
                    <p>Total Estimated Cost: {formatMoney(calculateTotal())}</p>
                  </div>
                  <div className="d-flex justify-content-center col-lg-12">
                    <button
                      type="submit"
                      className="btn btn-success col-lg-12"
                      disabled={!allRowsFilled() || loading}
                    >
                      {loading ? <Loader /> : "Submit Purchase Order"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};
export default CreatePurchaseOrder;
